import type { DirectKgQuantityMode } from "@/lib/quantity";

export type { DirectKgQuantityMode } from "@/lib/quantity";

export type OrderStatus =
  | "open"
  | "concluido"
  | "carregamento"
  | "change_requested"
  | "encerrado"
  | "cancelled";

export interface Order {
  id: string;
  client_id: string;
  store_id: string;
  quotation_id?: string | null;
  status: OrderStatus;
  version: number;
  obs?: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  deleted_at?: string | null;
  deleted_by?: string | null;
  items?: OrderItem[];
}

export interface OrderItem {
  id: string;
  order_id: string;
  quotation_item_id: string;
  quantity: number;
  direct_kg_quantity_mode?: DirectKgQuantityMode | null;
  bonus_quantity?: number | null;
  real_price: number;
  final_price: number;
  is_available: boolean;
  return_status: "NONE" | "DECISION" | "SUBSTITUIDO" | "DEVOLVIDO";
  return_note?: string | null;
  return_request_note?: string | null;
  return_decision_note?: string | null;
  return_requested_at?: string | null;
  return_requested_by?: string | null;
  return_decided_at?: string | null;
  return_decided_by?: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  deleted_at?: string | null;
  deleted_by?: string | null;
}

export interface EnrichedOrderItem extends OrderItem {
  product_id: string;
  supplier_id: string;
  product_name: string;
  supplier_name: string;
  price_per_box?: number;
  price_per_kg?: number;
  price_per_unit: number;
  computed_price: number;
  charged_price: number;
  billable_quantity?: number | null;
  bonus_value?: number | null;
  unit_type: string;
  received_quantity: number;
}

export interface OrderLedgerMeta extends Omit<Order, "items" | "quotation_id"> {
  store_name: string;
  client_name: string;
  quotation_id: string;
  quotation_date: string;
  checkin_id?: string | null;
  pending_change_request_id?: string;
}

export interface OrderSummary extends OrderLedgerMeta {
  total_price: number;
  item_count: number;
}

export interface EnrichedOrder extends OrderLedgerMeta {
  return_summary: {
    decision: number;
    substituido: number;
    devolvido: number;
  };
  items: EnrichedOrderItem[];
}

export interface OrderSummaryFilters {
  client_id?: string;
  store_id?: string;
  status?: OrderStatus;
  statuses?: OrderStatus[];
  search?: string;
  date_from?: string;
  date_to?: string;
}

export interface AddItemToOrderPayload {
  store_id: string;
  quotation_item_id: string;
  quantity: number;
}

export interface UpdateOrderItemPayload {
  quantity?: number;
  final_price?: number;
}

export interface SetOrderItemBonusPayload {
  bonus_quantity: number;
}

export type OriginalHandling =
  | "keep_received"
  | "keep_and_receive_more"
  | "return_all";

export interface ReplacementItemPayload {
  product_id: string;
  supplier_id: string;
  unit_type: string;
  quantity: number;
  real_price: number;
  final_price: number;
}

export interface ReplaceOrderItemPayload extends ReplacementItemPayload {
  expected_version: number;
  original_handling: OriginalHandling;
  additional_original_quantity?: number;
}

export interface RemoveOrderItemResponse {
  order_deleted: boolean;
  checkin_deleted: boolean;
}

export interface LegacyKgMigrationReturnLotPreview {
  return_lot_id: string;
  quantity: number;
  status: string;
  requires_reconciliation: boolean;
}

export interface LegacyKgMigrationCheckinPreview {
  checkin_id: string;
  checkin_item_id: string;
  expected_quantity: number;
  received_quantity: number;
  requires_received_quantity_reconciliation: boolean;
  return_lots: LegacyKgMigrationReturnLotPreview[];
}

export interface LegacyKgMigrationPreview {
  order_id: string;
  order_item_id: string;
  quantity: number;
  bonus_quantity: number;
  checkin?: LegacyKgMigrationCheckinPreview;
}

export interface LegacyKgMigrationReturnLotReconciliation {
  return_lot_id: string;
  quantity: number;
}

export interface LegacyKgMigrationReconciliation {
  received_quantity?: number;
  return_lots?: LegacyKgMigrationReturnLotReconciliation[];
}

export interface LegacyKgMigrationPayload {
  quantity: number;
  bonus_quantity?: number;
  final_price?: number;
  reconciliation?: LegacyKgMigrationReconciliation;
}

export type LegacyKgMigrationErrorCode =
  | "legacy_kg_migration_target_not_found"
  | "legacy_kg_migration_invalid_payload"
  | "legacy_kg_migration_checkin_closed"
  | "legacy_kg_migration_required";

export interface PageResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export type ChangeRequestStatus = "pending" | "approved" | "rejected";

export interface ChangeRequest {
  id: string;
  order_id: string;
  status: ChangeRequestStatus;
  requester_id: string;
  approver_id?: string | null;
  changes_payload: ChangePayload;
  rejection_reason?: string | null;
  expected_version: number;
  created_at: string;
  updated_at: string;
  updated_by: string;
  resolved_at?: string | null;
}

export interface AddItem {
  quotation_item_id: string;
  quantity: number;
  final_price?: number;
}

export interface ChangeRequestItem {
  id: string;
  product_name: string;
  supplier_name: string;
  unit_type: string;
}

export interface UpdateItem {
  order_item_id: string;
  quantity?: number;
  final_price?: number;
}

export interface ChangePayload {
  add?: AddItem[];
  remove_add?: string[];
  remove?: string[];
  update?: UpdateItem[];
}

export interface CreateChangeRequestPayload {
  changes_payload: ChangePayload;
}

export interface RejectChangeRequestPayload {
  rejection_reason: string;
}

export interface OrderSupplierQuantity {
  supplier_id: string;
  supplier_name: string;
  quantity: number;
}

export interface OrderStoreQuantity {
  store_id: string;
  store_name: string;
  total_quantity: number;
  by_supplier: OrderSupplierQuantity[];
}

export interface OrderOverview {
  date: string;
  total_quantity: number;
  by_store: OrderStoreQuantity[];
  by_supplier: OrderSupplierQuantity[];
}

export interface OrderAuditEntry {
  id: number;
  order_id: string;
  actor_id: string;
  action: string;
  before_state?: unknown;
  after_state?: unknown;
  metadata?: unknown;
  created_at: string;
}

export type OperationalPresentation = "operational";

export interface OperationalPageResponse<T> {
  presentation: OperationalPresentation;
  items: T[];
  count: number;
  limit: number;
  offset: number;
}

export interface OperationalSupplierQuantity {
  presentation: OperationalPresentation;
  supplier_id: string;
  supplier_name: string;
  quantity: number;
}

export interface OperationalStoreQuantity {
  presentation: OperationalPresentation;
  store_id: string;
  store_name: string;
  quantity: number;
  by_supplier: OperationalSupplierQuantity[];
}

export interface OperationalOrderOverview {
  presentation: OperationalPresentation;
  date: string;
  quantity: number;
  by_store: OperationalStoreQuantity[];
  by_supplier: OperationalSupplierQuantity[];
}

export interface OperationalOrderItem {
  presentation: OperationalPresentation;
  id: string;
  order_id: string;
  quotation_item_id: string;
  quantity: number;
  direct_kg_quantity_mode?: DirectKgQuantityMode | null;
  bonus_quantity: number;
  confirmed_quantity?: number | null;
  is_available: boolean;
  return_status: "NONE" | "DECISION" | "SUBSTITUIDO" | "DEVOLVIDO";
  return_note?: string | null;
  return_request_note?: string | null;
  return_decision_note?: string | null;
  return_requested_at?: string | null;
  return_requested_by?: string | null;
  return_decided_at?: string | null;
  return_decided_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface OperationalOrder {
  presentation: OperationalPresentation;
  id: string;
  client_id: string;
  store_id: string;
  quotation_id?: string | null;
  status: OrderStatus;
  version: number;
  obs?: string | null;
  created_at: string;
  updated_at: string;
  items?: OperationalOrderItem[];
}

export interface OperationalEnrichedOrderItem {
  presentation: OperationalPresentation;
  id: string;
  order_id: string;
  quotation_item_id: string;
  product_name: string;
  supplier_name: string;
  unit_type: string;
  quantity: number;
  direct_kg_quantity_mode?: DirectKgQuantityMode | null;
  bonus_quantity: number;
  confirmed_quantity?: number | null;
  is_available: boolean;
  return_status: "NONE" | "DECISION" | "SUBSTITUIDO" | "DEVOLVIDO";
  return_note?: string | null;
  return_request_note?: string | null;
  return_decision_note?: string | null;
  return_requested_at?: string | null;
  return_requested_by?: string | null;
  return_decided_at?: string | null;
  return_decided_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface OperationalReturnSummary {
  presentation: OperationalPresentation;
  decision: number;
  substituted: number;
  returned: number;
}

export interface OperationalEnrichedOrder {
  presentation: OperationalPresentation;
  id: string;
  client_id: string;
  store_id: string;
  store_name: string;
  client_name: string;
  quotation_id: string;
  quotation_date: string;
  status: OrderStatus;
  version: number;
  obs?: string | null;
  checkin_id?: string | null;
  pending_change_request_id?: string;
  return_summary: OperationalReturnSummary;
  items: OperationalEnrichedOrderItem[];
  created_at: string;
  updated_at: string;
}

export interface OperationalOrderSummary {
  presentation: OperationalPresentation;
  id: string;
  client_id: string;
  store_id: string;
  store_name: string;
  client_name: string;
  quotation_id: string;
  quotation_date: string;
  status: OrderStatus;
  version: number;
  obs?: string | null;
  checkin_id?: string | null;
  pending_change_request_id?: string;
  item_count: number;
  created_at: string;
  updated_at: string;
}

export interface OperationalChangeRequestAddItem {
  presentation: OperationalPresentation;
  quotation_item_id: string;
  quantity: number;
}

export interface OperationalChangeRequestUpdateItem {
  presentation: OperationalPresentation;
  order_item_id: string;
  quantity: number;
}

export interface OperationalChangeRequestChanges {
  presentation: OperationalPresentation;
  add?: OperationalChangeRequestAddItem[];
  update?: OperationalChangeRequestUpdateItem[];
}

export interface OperationalChangeRequest {
  presentation: OperationalPresentation;
  id: string;
  order_id: string;
  status: ChangeRequestStatus;
  requester_id: string;
  approver_id?: string | null;
  rejection_reason?: string | null;
  expected_version: number;
  quantity_changes?: OperationalChangeRequestChanges | null;
  created_at: string;
  updated_at: string;
  resolved_at?: string | null;
}

export interface OperationalAuditEntry {
  presentation: OperationalPresentation;
  id: number;
  order_id: string;
  actor_id: string;
  action: string;
  created_at: string;
}

export type OrderResponse = Order | OperationalOrder;
export type OrderItemResponse = OrderItem | OperationalOrderItem;
export type EnrichedOrderItemResponse =
  | EnrichedOrderItem
  | OperationalEnrichedOrderItem;
export type EnrichedOrderResponse = EnrichedOrder | OperationalEnrichedOrder;
export type OrderSummaryResponse = OrderSummary | OperationalOrderSummary;
export type ChangeRequestResponse = ChangeRequest | OperationalChangeRequest;
export type OrderOverviewResponse = OrderOverview | OperationalOrderOverview;
export type OrderAuditEntryResponse = OrderAuditEntry | OperationalAuditEntry;

export type OrderPageResponse =
  | PageResponse<Order>
  | OperationalPageResponse<OperationalOrder>;
export type EnrichedOrderPageResponse =
  | PageResponse<EnrichedOrder>
  | OperationalPageResponse<OperationalEnrichedOrder>;
export type OrderSummaryPageResponse =
  | PageResponse<OrderSummary>
  | OperationalPageResponse<OperationalOrderSummary>;
export type ChangeRequestPageResponse =
  | PageResponse<ChangeRequest>
  | OperationalPageResponse<OperationalChangeRequest>;
export type OrderAuditEntryPageResponse =
  | PageResponse<OrderAuditEntry>
  | OperationalPageResponse<OperationalAuditEntry>;

export function hasOperationalPresentation(
  value: unknown,
): value is { presentation: OperationalPresentation } {
  return (
    typeof value === "object" &&
    value !== null &&
    "presentation" in value &&
    value.presentation === "operational"
  );
}

export function isOperationalOrder(value: unknown): value is OperationalOrder {
  return hasOperationalPresentation(value);
}

export function isOperationalEnrichedOrder(
  value: unknown,
): value is OperationalEnrichedOrder {
  return hasOperationalPresentation(value);
}

export function isFinancialEnrichedOrder(
  value: EnrichedOrderResponse,
): value is EnrichedOrder {
  return !isOperationalEnrichedOrder(value);
}

export function isOperationalOrderSummary(
  value: unknown,
): value is OperationalOrderSummary {
  return hasOperationalPresentation(value);
}

export function isOperationalOrderSummaryPage(
  value: unknown,
): value is OperationalPageResponse<OperationalOrderSummary> {
  return hasOperationalPresentation(value);
}

export function isOperationalChangeRequest(
  value: unknown,
): value is OperationalChangeRequest {
  return hasOperationalPresentation(value);
}

export function isFinancialChangeRequest(
  value: ChangeRequestResponse,
): value is ChangeRequest {
  return !isOperationalChangeRequest(value);
}

export function isOperationalEnrichedOrderPage(
  value: unknown,
): value is OperationalPageResponse<OperationalEnrichedOrder> {
  return hasOperationalPresentation(value);
}
